// Tiny column-major mat4 / vec3 helpers for the globe renderer (no dependencies).

import type { Vec3 } from "../../shared/geo";

export type Mat4 = Float32Array;

export const DEG = Math.PI / 180;

export function mat4(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

/** out = a * b (column-major, so b is applied first). */
export function mul4(out: Mat4, a: Mat4, b: Mat4): Mat4 {
  const r = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let rr = 0; rr < 4; rr++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + rr]! * b[c * 4 + k]!;
      r[c * 4 + rr] = s;
    }
  }
  out.set(r);
  return out;
}

/** General 4x4 inverse. Returns false (and leaves out untouched) when singular. */
export function invert4(out: Mat4, m: Mat4): boolean {
  const a00 = m[0]!, a01 = m[1]!, a02 = m[2]!, a03 = m[3]!;
  const a10 = m[4]!, a11 = m[5]!, a12 = m[6]!, a13 = m[7]!;
  const a20 = m[8]!, a21 = m[9]!, a22 = m[10]!, a23 = m[11]!;
  const a30 = m[12]!, a31 = m[13]!, a32 = m[14]!, a33 = m[15]!;
  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;
  let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (!det) return false;
  det = 1 / det;
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
  return true;
}

/** Transforms (x, y, z, w) by m, returning a new 4-tuple. */
export function xform4(m: Mat4, x: number, y: number, z: number, w = 1): [number, number, number, number] {
  return [
    m[0]! * x + m[4]! * y + m[8]! * z + m[12]! * w,
    m[1]! * x + m[5]! * y + m[9]! * z + m[13]! * w,
    m[2]! * x + m[6]! * y + m[10]! * z + m[14]! * w,
    m[3]! * x + m[7]! * y + m[11]! * z + m[15]! * w,
  ];
}

/**
 * Rotation that brings the point (lat, lon) to face +Z with north up:
 * R = Rx(lat) * Ry(-lon).
 */
export function globeRotation(latDeg: number, lonDeg: number): Mat4 {
  const a = latDeg * DEG;
  const t = -lonDeg * DEG;
  const ca = Math.cos(a), sa = Math.sin(a);
  const ct = Math.cos(t), st = Math.sin(t);
  // Ry(t): x' = x ct + z st ; z' = -x st + z ct
  const ry = mat4();
  ry[0] = ct; ry[2] = -st; ry[8] = st; ry[10] = ct;
  // Rx(a): y' = y ca - z sa ; z' = y sa + z ca
  const rx = mat4();
  rx[5] = ca; rx[6] = sa; rx[9] = -sa; rx[10] = ca;
  return mul4(mat4(), rx, ry);
}

export function translation(x: number, y: number, z: number): Mat4 {
  const m = mat4();
  m[12] = x; m[13] = y; m[14] = z;
  return m;
}

/**
 * Perspective projection whose frustum is shifted so that clip-space (0,0) lands
 * at NDC (ox, oy). fy is the focal length (1 / tan(fovy/2)).
 */
export function shiftedPerspective(fy: number, aspect: number, near: number, far: number, ox: number, oy: number): Mat4 {
  const m = new Float32Array(16);
  m[0] = fy / aspect;
  m[5] = fy;
  m[8] = -ox;
  m[9] = -oy;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

export function norm3(v: Vec3): Vec3 {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

export function dot3(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function lerpAngleDeg(a: number, b: number, t: number): number {
  const d = (((b - a) % 360) + 540) % 360 - 180;
  return a + d * t;
}

export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** HSL → linear-ish RGB triple in 0..1. h in degrees. */
export function hsl(h: number, s: number, l: number): Vec3 {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  return [r + m, g + m, b + m];
}

/** Cheap deterministic hash of a string to 0..1. */
export function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}
